import React, { createContext, useContext, useState, useEffect } from 'react';
import { getHotelData, updateDataSection, updateDataSections, revertToOriginal, exportHotelData } from '../utils/dataManager';
import {
  fetchHotelDataFromDB,
  isDatabaseInitialized,
  replaceAllMenuDataInDB,
  updateMenuItemsInDB,
  updateMenuSectionOrderInDB,
  updateMenuSectionNamesInDB,
  updateOpeningHoursInDB,
  updateEventsInDB
} from '../utils/supabaseDataManager';

const DataContext = createContext();

export const useData = () => {
  const context = useContext(DataContext);
  if (!context) {
    throw new Error('useData must be used within a DataProvider');
  }
  return context;
};

export const DataProvider = ({ children }) => {
  const [hotelData, setHotelData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [usingDatabase, setUsingDatabase] = useState(false);

  // Load initial data
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Check if Supabase is available and configured
      const hasSupabaseConfig = process.env.REACT_APP_SUPABASE_URL && process.env.REACT_APP_SUPABASE_ANON_KEY;
      
      if (hasSupabaseConfig) {
        // Try to check if database is initialized
        const dbInitialized = await isDatabaseInitialized();
        
        if (dbInitialized) {
          console.log('Using Supabase database for data');
          const result = await fetchHotelDataFromDB();
          if (result.success) {
            setHotelData(result.data);
            setUsingDatabase(true);
            setLoading(false);
            return;
          } else {
            console.warn('Failed to fetch from database, falling back to JSON:', result.error);
          }
        }
      }
      
      // Fall back to JSON file
      console.log('Using JSON file for data');
      const result = await getHotelData();
      if (result.success) {
        setHotelData(result.data);
        setUsingDatabase(false);
      } else {
        setError(result.error);
      }
    } catch (error) {
      console.error('Error in loadData:', error);
      // Fall back to JSON file
      const result = await getHotelData();
      if (result.success) {
        setHotelData(result.data);
        setUsingDatabase(false);
      } else {
        setError(error.message);
      }
    }
    
    setLoading(false);
  };

  const updateSection = async (section, data) => {
    setLoading(true);
    setError(null);

    try {
      // If using database, update specific sections in database
      if (usingDatabase) {
        let dbResult = { success: true };
        
        switch (section) {
          case 'menuItems':
            dbResult = await updateMenuItemsInDB(data);
            break;
          case 'menuSectionOrder':
            dbResult = await updateMenuSectionOrderInDB(data);
            break;
          case 'menuSectionNames':
            dbResult = await updateMenuSectionNamesInDB(data);
            break;
          case 'openingHours':
            dbResult = await updateOpeningHoursInDB(data);
            break;
          case 'events':
            dbResult = await updateEventsInDB(data);
            break;
          default:
            // For other sections, use the generic method
            dbResult = await updateDataSection(section, data);
            break;
        }

        if (dbResult.success) {
          // Refresh data from database to get updated state
          const refreshResult = await fetchHotelDataFromDB();
          if (refreshResult.success) {
            setHotelData(refreshResult.data);
            setLoading(false);
            return { success: true };
          } else {
            throw new Error('Failed to refresh data after update');
          }
        } else {
          throw new Error(dbResult.error);
        }
      } else {
        // Fall back to localStorage method
        const result = await updateDataSection(section, data);
        setLoading(false);
        if (result.success) {
          setHotelData(result.data);
          return { success: true };
        } else {
          throw new Error(result.error);
        }
      }
    } catch (error) {
      setLoading(false);
      setError(error.message);
      return { success: false, error: error.message };
    }
  };

  const revertData = async () => {
    setLoading(true);
    setError(null);

    const result = await revertToOriginal();
    setLoading(false);
    if (result.success) {
      setHotelData(result.data);
      return { success: true };
    } else {
      setError(result.error);
      return { success: false, error: result.error };
    }
  };

  const saveMenuData = async (menuItems, menuSectionOrder, menuSectionNames) => {
    setLoading(true);
    setError(null);

    try {
      if (usingDatabase) {
        const replaceResult = await replaceAllMenuDataInDB(menuItems, menuSectionOrder, menuSectionNames);
        if (!replaceResult.success) throw new Error(replaceResult.error);

        const refreshResult = await fetchHotelDataFromDB();
        if (!refreshResult.success) throw new Error('Failed to refresh data after update');
        setHotelData(refreshResult.data);
        setLoading(false);
        return { success: true };
      } else {
        const result = await updateDataSections({
          menuItems,
          menuSectionOrder,
          menuSectionNames
        });
        setLoading(false);
        if (result.success) {
          setHotelData(result.data);
          return { success: true };
        } else {
          throw new Error(result.error);
        }
      }
    } catch (error) {
      setLoading(false);
      setError(error.message);
      return { success: false, error: error.message };
    }
  };

  const exportData = async () => {
    const result = await exportHotelData();
    if (!result.success) {
      setError(result.error);
    }
    return result;
  };

  const value = {
    hotelData,
    loading,
    error,
    usingDatabase,
    updateSection,
    saveMenuData,
    revertData,
    exportData,
    refreshData: loadData
  };

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  );
};